import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../services/supabaseClient';
import { Task, TaskSubmission } from '../../types/task';
import TaskVerification from '../../components/tasks/TaskVerification';
import ResponsiveContainer from '../../components/ui/ResponsiveContainer';
import { useAuth } from '../../context/AuthContext';
import { VerificationService } from '../../services/verificationService';
import { showToast } from '../../utils/toast';
import '../../styles/pages/TaskDetailPage.css';

const getRelativePath = (urlOrPath: string): string => {
  if (!urlOrPath) return ''; // Handle null or undefined input
  if (urlOrPath.startsWith('http')) {
    try {
      const url = new URL(urlOrPath);
      const parts = url.pathname.split('/screenshots/');
      if (parts.length > 1) {
        const relative = parts.pop();
        return relative || ''; // Ensure not undefined
      }
    } catch (e) {
      console.error("Error parsing URL, assuming it's a path:", urlOrPath, e);
      // If URL parsing fails, it might be a malformed URL or already a path segment
      // Attempt to extract the last part if it looks like a path with bucket name
      const bucketAndPath = urlOrPath.split('/screenshots/');
      if (bucketAndPath.length > 1) {
        return bucketAndPath.pop() || '';
      }
      return urlOrPath; // Fallback
    }
  }
  return urlOrPath; // Assume it's already a relative path
};

const TaskDetailPage: React.FC = () => {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const [task, setTask] = useState<Task | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [verificationSubmitted, setVerificationSubmitted] = useState(false);
  const [submissions, setSubmissions] = useState<TaskSubmission[]>([]);
  const [userSubmission, setUserSubmission] = useState<TaskSubmission | null>(null);
  const [submissionsLoading, setSubmissionsLoading] = useState(false);
  const [reviewFeedback, setReviewFeedback] = useState('');
  const [reviewSuccess, setReviewSuccess] = useState<string | null>(null);
  const [reviewError, setReviewError] = useState<string | null>(null);
  const [screenshotDisplayUrl, setScreenshotDisplayUrl] = useState<string | null>(null);
  const [submissionScreenshotUrls, setSubmissionScreenshotUrls] = useState<Record<string, string | null>>({});
  const [taskDeleting, setTaskDeleting] = useState(false);
  const [taskCancelling, setTaskCancelling] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [currentSubmissionIndex, setCurrentSubmissionIndex] = useState(0);
  const [expandedSections, setExpandedSections] = useState<{
    approved: boolean;
    rejected: boolean;
    pending: boolean;
  }>({
    approved: false,
    rejected: false,
    pending: false // All sections collapsed by default
  });

  const { user } = useAuth();

  // Fetch task details from Supabase
  useEffect(() => {
    const fetchTaskDetails = async () => {
      if (!taskId) return;

      try {
        setLoading(true);
        setError(null);

        const { data, error } = await supabase
          .from('tasks')
          .select('*')
          .eq('id', taskId)
          .single();

        if (error) throw error;
        if (!data) throw new Error('Task not found');

        setTask(data as Task);
      } catch (err) {
        console.error('Error fetching task details:', err);
        setError('Failed to load task details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchTaskDetails();
  }, [taskId, verificationSubmitted]);

  // Fetch task submissions
  useEffect(() => {
    const fetchSubmissions = async () => {
      if (!taskId || !user) return;

      try {
        setSubmissionsLoading(true);

        // Fetch all submissions for this task if user is the creator
        if (task && task.created_by === user.id) {
          const { data, error } = await supabase
            .from('task_submissions')
            .select('*')
            .eq('task_id', taskId);

          if (error) throw error;
          setSubmissions(data as TaskSubmission[]);

          // Reset current submission index if submissions changed
          setCurrentSubmissionIndex(0);
        }

        // Fetch the current user's submission for this task
        const { data: userSubmissionData, error: userSubmissionError } = await supabase
          .from('task_submissions')
          .select('*')
          .eq('task_id', taskId)
          .eq('user_id', user.id)
          .maybeSingle();

        if (userSubmissionError) throw userSubmissionError;
        setUserSubmission(userSubmissionData as TaskSubmission);

      } catch (err) {
        console.error('Error fetching submissions:', err);
      } finally {
        setSubmissionsLoading(false);
      }
    };

    if (task) {
      fetchSubmissions();
    }
  }, [taskId, user, task, verificationSubmitted]);

  useEffect(() => {
    if (user && userSubmission?.proof?.screenshot_url && userSubmission.verification_type === 'screenshot') {
      const path = getRelativePath(userSubmission.proof.screenshot_url);
      if (!path) {
        console.error('Could not determine a valid path for user submission screenshot:', userSubmission.proof.screenshot_url);
        setScreenshotDisplayUrl(null);
        return;
      }
      const generateSignedUrl = async () => {
        const { data, error: signedUrlError } = await supabase.storage
          .from('screenshots')
          .createSignedUrl(path, 60 * 5); // Expires in 5 minutes

        if (signedUrlError) {
          console.error('Error generating signed URL for screenshot:', signedUrlError);
          setScreenshotDisplayUrl(null);
        } else if (data) {
          setScreenshotDisplayUrl(data.signedUrl);
        }
      };
      generateSignedUrl();
    } else {
      setScreenshotDisplayUrl(null); // Reset if no valid submission/path
    }
  }, [user, userSubmission]);

  useEffect(() => {
    const newUrls: Record<string, string | null> = {}; // Initialize an empty object for current batch
    if (submissions && submissions.length > 0) {
      const generateAllUrls = async () => {
        for (const sub of submissions) {
          if (sub.verification_type === 'screenshot' && sub.proof?.screenshot_url) {
            const path = getRelativePath(sub.proof.screenshot_url);
            if (!path) {
              console.error(`Could not determine a valid path for submission ${sub.id} screenshot:`, sub.proof.screenshot_url);
              newUrls[sub.id] = null;
              continue; // Skip to next submission
            }
            try {
              const { data, error: signedUrlGenError } = await supabase.storage // Renamed 'error'
                .from('screenshots')
                .createSignedUrl(path, 300); // 300 seconds = 5 minutes

              if (signedUrlGenError) {
                console.error(`Error generating signed URL for submission ${sub.id} (path: ${path}):`, signedUrlGenError);
                newUrls[sub.id] = null;
              } else if (data && data.signedUrl) {
                newUrls[sub.id] = data.signedUrl;
              } else {
                console.warn(`No signed URL or data returned for submission ${sub.id} (path: ${path})`, data);
                newUrls[sub.id] = null;
              }
            } catch (e) {
              console.error(`Exception generating signed URL for submission ${sub.id} (path: ${path}):`, e);
              newUrls[sub.id] = null;
            }
          } else {
            // If not a screenshot or no path, ensure its entry is null if it's supposed to be a screenshot
            if (sub.verification_type === 'screenshot') {
              newUrls[sub.id] = null;
            }
          }
        }
        setSubmissionScreenshotUrls(newUrls); // Replace the whole map
      };
      generateAllUrls();
    } else {
      setSubmissionScreenshotUrls({}); // Clear if no submissions
    }
  }, [submissions, supabase]); // Added supabase to dependencies

  // Handle verification submission
  const handleVerificationSubmitted = async () => {
    setVerificationSubmitted(true);

    // Show success message and redirect to tasks page to see the submitted task
    if (task) {
      try {
        // Small delay to ensure submission is processed
        setTimeout(() => {
          showToast('Task submitted successfully! You can view your submission in the Completed Tasks tab.', 'success');
          window.location.href = '/tasks'; // Redirect to tasks page with fresh data
        }, 1000);
      } catch (err) {
        console.error('Error handling task submission:', err);
      }
    }
  };

  // Format status text
  const formatStatus = (status: string) => {
    return status.replace(/_/g, ' ');
  };

  // Handle task review (approve or reject)
  const handleReviewSubmission = async (submissionUserId: string, approved: boolean) => {
    if (!task || !user) return;

    setReviewSuccess(null);
    setReviewError(null);

    try {
      const { success, error } = await VerificationService.reviewSubmission(
        task.id,
        submissionUserId,
        approved,
        user.id,
        reviewFeedback
      );

      if (error) throw error;

      setReviewSuccess(`Submission ${approved ? 'approved' : 'rejected'} successfully`);
      setReviewFeedback('');

      // Refresh submissions
      setVerificationSubmitted(!verificationSubmitted);
    } catch (err) {
      console.error('Review error:', err);
      setReviewError(`Failed to ${approved ? 'approve' : 'reject'} submission. Please try again.`);
    }
  };

  // Handle updating a submission with new proof
  const handleUpdateSubmission = () => {
    // Reset the verification submitted state to trigger the verification component
    setVerificationSubmitted(false);
  };

  // Task management functions
  const handleDeleteTask = async () => {
    if (!task || !user || task.created_by !== user.id) return;

    // Check for any submissions - should not delete if any exist
    if (submissions.length > 0) {
      const approvedSubmissions = submissions.filter(s => s.status === 'approved');
      if (approvedSubmissions.length > 0) {
        showToast('Cannot delete task - approved work exists. This task is protected.', 'warning');
        return;
      } else {
        showToast('Cannot delete task - submissions exist. Use cancel with compensation instead.', 'warning');
        return;
      }
    }

    console.log('Starting task deletion for task:', task.id, 'country:', task.country_code);

    setTaskDeleting(true);
    try {
      const { error, data } = await supabase
        .from('tasks')
        .delete()
        .eq('id', task.id)
        .select(); // Add select to get the deleted row data

      if (error) {
        console.error('Delete error:', error);
        throw error;
      }

      console.log('Task deleted successfully:', data);
      console.log('Navigating to /tasks...');

      navigate('/tasks');
    } catch (err) {
      console.error('Error deleting task:', err);
      setError('Failed to delete task. Please try again.');
    } finally {
      setTaskDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleCancelTaskWithPayment = async () => {
    if (!task || !user || task.created_by !== user.id) return;

    // Check for approved submissions - should not cancel if any exist
    const approvedSubmissions = submissions.filter(s => s.status === 'approved');
    const pendingSubmissions = submissions.filter(s => s.status === 'pending');
    
    if (approvedSubmissions.length > 0) {
      showToast('Cannot cancel task - approved work exists. This task is protected.', 'warning');
      return;
    }

    if (pendingSubmissions.length === 0) {
      showToast('No pending submissions to compensate.', 'info');
      return;
    }

    setTaskCancelling(true);
    try {
      // Calculate total payment needed (only for pending submissions)
      const totalPayment = pendingSubmissions.length * task.points;

      // Check if user has enough points
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('points')
        .eq('id', user.id)
        .single();

      if (userError) throw userError;

      if (userData.points < totalPayment) {
        throw new Error(`Insufficient points. You need ${totalPayment} points but only have ${userData.points}.`);
      }

      // Start a transaction-like process
      // 1. Deduct points from creator
      const { error: deductError } = await supabase
        .from('users')
        .update({ points: userData.points - totalPayment })
        .eq('id', user.id);

      if (deductError) throw deductError;

      // 2. Award points to each pending submission user
      for (const submission of pendingSubmissions) {
        const { error: awardError } = await supabase.rpc('increment_user_points', {
          user_id_param: submission.user_id,
          points_param: task.points
        });

        if (awardError) {
          console.error('Error awarding points to user:', submission.user_id, awardError);
          // Continue with other users even if one fails
        }
      }

      // 3. Update task status to cancelled
      const { error: updateError } = await supabase
        .from('tasks')
        .update({ status: 'rejected' }) // Using 'rejected' as cancelled status
        .eq('id', task.id);

      if (updateError) throw updateError;

      // 4. Update all pending submissions to approved (since they got paid)
      const { error: submissionUpdateError } = await supabase
        .from('task_submissions')
        .update({
          status: 'approved',
          feedback: 'Task cancelled by creator - compensation paid'
        })
        .eq('task_id', task.id)
        .eq('status', 'pending'); // Only update pending submissions

      if (submissionUpdateError) throw submissionUpdateError;

      setTask({ ...task, status: 'cancelled' });
      showToast(`Task cancelled successfully. ${totalPayment} points paid to ${pendingSubmissions.length} applicant(s).`, 'success');

    } catch (err) {
      console.error('Error cancelling task:', err);
      setError(err instanceof Error ? err.message : 'Failed to cancel task. Please try again.');
    } finally {
      setTaskCancelling(false);
      setShowCancelConfirm(false);
    }
  };

  const resetVerificationForm = () => {
    setVerificationSubmitted(false);
  };

  // Render loading state
  if (loading) {
    return (
      <ResponsiveContainer>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </ResponsiveContainer>
    );
  }

  // Render error state
  if (error || !task) {
    return (
      <ResponsiveContainer>
        <div className="alert alert-danger mt-4" role="alert">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error || 'Task not found'}
        </div>
      </ResponsiveContainer>
    );
  }

  return (
    <div className="task-detail-page">
      <ResponsiveContainer>
        <div className="task-detail-card">
          <div className="task-detail-header">
            <div>
              <h1 className="task-detail-title">{task.title}</h1>
              <div className="task-detail-verification">
                <i className={`bi bi-${task.verification_type === 'email_username' ? 'envelope' :
                  task.verification_type === 'text_response' ? 'chat-text' : 'image'}`}></i>
                {task.verification_type === 'email_username' ? 'Email/Username Verification' :
                  task.verification_type === 'text_response' ? 'Text Response Verification' : 'Screenshot Verification'}
              </div>
            </div>

            <div className={`task-detail-status task-detail-status-${task.status}`}>
              {formatStatus(task.status)}
            </div>
          </div>

          <div className="task-detail-info">
            <div className="task-detail-info-item">
              <span className="task-detail-info-label">Reward</span>
              <span className="task-detail-info-value task-detail-points">{task.points} points</span>
            </div>

            <div className="task-detail-info-item">
              <span className="task-detail-info-label">Category</span>
              <span className="task-detail-info-value">{task.category}</span>
            </div>

            <div className="task-detail-info-item">
              <span className="task-detail-info-label">Created</span>
              <span className="task-detail-info-value">
                {new Date(task.created_at).toLocaleString()}
              </span>
            </div>

            <div className="task-detail-info-item">
              <span className="task-detail-info-label">Participants</span>
              <span className="task-detail-info-value">
                {task.current_participants}
                {task.max_participants ? ` / ${task.max_participants}` : ''}
              </span>
            </div>
          </div>

          {/* Task Management Controls (for task creators only) */}
          {user && task && task.created_by === user.id && task.status === 'open' && (
            <div className="task-management-section">
              {(() => {
                const approvedSubmissions = submissions.filter(s => s.status === 'approved');
                const pendingSubmissions = submissions.filter(s => s.status === 'pending');
                const totalSubmissions = submissions.length;
                const hasApprovedWork = approvedSubmissions.length > 0;
                
                return (
                  <>
                    <div className="alert alert-info">
                      <i className="bi bi-info-circle me-2"></i>
                      <strong>Task Management:</strong>
                      {totalSubmissions === 0
                        ? ' No submissions yet - you can delete this task.'
                        : hasApprovedWork
                        ? ` ${approvedSubmissions.length} approved submission(s). Task cannot be deleted once work is approved.`
                        : ` ${totalSubmissions} pending submission(s) - you can cancel and pay compensation.`
                      }
                    </div>

                    <div className="task-management-actions">
                      {totalSubmissions === 0 ? (
                        <button
                          className="btn btn-outline-danger"
                          onClick={() => setShowDeleteConfirm(true)}
                          disabled={taskDeleting}
                        >
                          <i className="bi bi-trash me-2"></i>
                          {taskDeleting ? 'Deleting...' : 'Delete Task'}
                        </button>
                      ) : hasApprovedWork ? (
                        <div className="alert alert-warning mb-0">
                          <i className="bi bi-shield-exclamation me-2"></i>
                          <strong>Task Protected:</strong> This task cannot be deleted or cancelled because approved work exists. Users have completed and been compensated for their submissions.
                        </div>
                      ) : (
                        <button
                          className="btn btn-outline-warning"
                          onClick={() => setShowCancelConfirm(true)}
                          disabled={taskCancelling}
                        >
                          <i className="bi bi-x-circle me-2"></i>
                          {taskCancelling ? 'Cancelling...' : `Cancel & Pay ${totalSubmissions * task.points} Points`}
                        </button>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>
          )}

          {/* Delete Confirmation Modal */}
          {showDeleteConfirm && (
            <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
              <div className="modal-dialog">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Confirm Task Deletion</h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setShowDeleteConfirm(false)}
                    ></button>
                  </div>
                  <div className="modal-body">
                    <p>Are you sure you want to delete this task?</p>
                    <p className="text-muted">This action cannot be undone. Since no submissions have been made yet, you can delete it without any cost.</p>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setShowDeleteConfirm(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={handleDeleteTask}
                      disabled={taskDeleting}
                    >
                      {taskDeleting ? 'Deleting...' : 'Delete Task'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Cancel with Payment Confirmation Modal */}
          {showCancelConfirm && (
            <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
              <div className="modal-dialog">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Confirm Task Cancellation</h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setShowCancelConfirm(false)}
                    ></button>
                  </div>
                  <div className="modal-body">
                    <p>Are you sure you want to cancel this task?</p>
                    <div className="alert alert-warning">
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      <strong>Payment Required:</strong>
                      <ul className="mb-0 mt-2">
                        <li>Pending submissions: {submissions.filter(s => s.status === 'pending').length}</li>
                        <li>Points per submission: {task.points}</li>
                        <li><strong>Total cost: {submissions.filter(s => s.status === 'pending').length * task.points} points</strong></li>
                      </ul>
                    </div>
                    <p className="text-muted">
                      Each applicant will receive {task.points} points as compensation for their time and effort.
                    </p>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setShowCancelConfirm(false)}
                    >
                      Keep Task Active
                    </button>
                    <button
                      type="button"
                      className="btn btn-warning"
                      onClick={handleCancelTaskWithPayment}
                      disabled={taskCancelling}
                    >
                      {taskCancelling ? 'Processing...' : 'Cancel & Pay Compensation'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <h3>Description</h3>
          <div className="task-detail-description">
            {task.description}
          </div>

          {task.tags && task.tags.length > 0 && (
            <div className="task-detail-tags">
              {task.tags.map((tag, index) => (
                <span key={index} className="task-detail-tag">{tag}</span>
              ))}
            </div>
          )}

          <div className="task-detail-instructions">
            <h4 className="task-detail-instructions-title">
              <i className="bi bi-lightbulb"></i> Instructions
            </h4>
            <p>{task.instructions}</p>
          </div>

          {task.verification_type === 'text_response' && task.verification_details.verification_question && (
            <div className="task-detail-verification-info">
              <h4 className="task-detail-verification-title">
                <i className="bi bi-chat-text"></i> Verification Question
              </h4>
              <p className="verification-question">
                After completing this task, you'll need to answer: <strong>{task.verification_details.verification_question}</strong>
              </p>
            </div>
          )}
        </div>

        {/* Task Creator Notice */}
        {user && task && task.created_by === user.id && task.status === 'open' && (
          <div className="task-detail-creator-notice">
            <div className="alert alert-info" role="alert">
              <i className="bi bi-info-circle me-2"></i>
              <strong>Task Publisher Notice:</strong> You cannot submit to your own tasks. You can review submissions from other users below.
            </div>
          </div>
        )}

        {/* User's own submission section - Show for new submissions or when updating */}
        {user && task.created_by !== user.id && ((!userSubmission || verificationSubmitted === false) && task.status === 'open') && (
          <div id="verificationSection">
            <TaskVerification
              task={task}
              userId={user.id}
              onVerificationSubmitted={handleVerificationSubmitted}
              existingSubmission={userSubmission}
            />
          </div>
        )}

        {/* Display user's own submission if they have one */}
        {user && task.created_by !== user.id && userSubmission && (
          <div className="user-submission-section">
            <h3>Your Submission</h3>
            <div className={`submission-card ${userSubmission.status === 'rejected' &&
                userSubmission.feedback?.includes('maximum number of participants')
                ? 'submission-rejected-full'
                : ''
              }`}>
              <div className="submission-header">
                <span className={`submission-status submission-status-${userSubmission.status}`}>
                  {formatStatus(userSubmission.status)}
                </span>
                <span className="submission-date">
                  Submitted: {new Date(userSubmission.submitted_at).toLocaleString()}
                </span>
              </div>

              <div className="submission-proof">
                <h4>Your Proof</h4>
                {userSubmission.verification_type === 'email_username' && userSubmission.proof?.email_username && (
                  <div className="proof-item">
                    <strong>Email/Username:</strong> {userSubmission.proof.email_username}
                  </div>
                )}

                {userSubmission.verification_type === 'text_response' && userSubmission.proof?.text_response && (
                  <div className="proof-item">
                    <strong>Response:</strong>
                    <div className="text-response">{userSubmission.proof.text_response}</div>
                  </div>
                )}

                {userSubmission.verification_type === 'screenshot' && screenshotDisplayUrl && (
                  <div className="proof-item">
                    <strong>Screenshot:</strong>
                    <div className="screenshot-container">
                      <img src={screenshotDisplayUrl} alt="Verification screenshot" className="proof-screenshot" />
                    </div>
                  </div>
                )}
              </div>

              {userSubmission.feedback && (
                <div className="submission-feedback">
                  <h4>Feedback</h4>
                  <p>{userSubmission.feedback}</p>
                </div>
              )}

              {userSubmission.status === 'rejected' && (
                <div className="submission-actions">
                  {/* Only show update button if task is still open and not full */}
                  {task.status === 'open' && !(task.max_participants && task.current_participants >= task.max_participants) ? (
                    <button className="btn btn-primary" onClick={handleUpdateSubmission}>
                      Update Submission
                    </button>
                  ) : (
                    <div className="update-disabled-message">
                      {userSubmission.feedback?.includes('maximum number of participants') ? (
                        <p className="text-muted">
                          <i className="bi bi-info-circle me-1"></i>
                          This task has reached its maximum number of participants. Updates are no longer allowed.
                        </p>
                      ) : task.status === 'completed' ? (
                        <p className="text-muted">
                          <i className="bi bi-info-circle me-1"></i>
                          This task has been completed. Updates are no longer allowed.
                        </p>
                      ) : (
                        <p className="text-muted">
                          <i className="bi bi-info-circle me-1"></i>
                          This task is no longer accepting submissions or updates.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Task creator's review section */}
        {user && task && task.created_by === user.id && submissions.length > 0 && (
          <div className="submissions-section">
            <h3>Task Submissions</h3>

            {reviewSuccess && (
              <div className="alert alert-success" role="alert">
                {reviewSuccess}
              </div>
            )}

            {reviewError && (
              <div className="alert alert-danger" role="alert">
                {reviewError}
              </div>
            )}

            {(() => {
              // Filter submissions by status
              const pendingSubmissions = submissions.filter(s => s.status === 'pending');
              const approvedSubmissions = submissions.filter(s => s.status === 'approved');
              const rejectedSubmissions = submissions.filter(s => s.status === 'rejected');

              const toggleSection = (section: 'approved' | 'rejected' | 'pending') => {
                setExpandedSections(prev => ({
                  ...prev,
                  [section]: !prev[section]
                }));
              };

              const renderSubmissionCard = (submission: TaskSubmission) => (
                <div key={submission.id} className="submission-card-clean">
                  <div className="submission-header-clean">
                    <span className="submission-user-clean">
                      Anonymous User #{submission.user_id.slice(-8)}
                    </span>
                    <span className="submission-date-clean">
                      {new Date(submission.submitted_at).toLocaleDateString()}
                    </span>
                  </div>

                  <div className="submission-proof-clean">
                    {submission.verification_type === 'email_username' && submission.proof?.email_username && (
                      <div className="proof-content">
                        <strong>Email/Username:</strong> {submission.proof.email_username}
                      </div>
                    )}
                    {submission.verification_type === 'text_response' && submission.proof?.text_response && (
                      <div className="proof-content">
                        <strong>Response:</strong> {submission.proof.text_response}
                      </div>
                    )}
                    {submission.verification_type === 'screenshot' && submission.proof?.screenshot_url && (
                      <div className="proof-content">
                        <strong>Screenshot:</strong>
                        {submissionScreenshotUrls[submission.id] ? (
                          <img
                            src={submissionScreenshotUrls[submission.id]!}
                            alt="Verification screenshot"
                            className="proof-screenshot-clean"
                          />
                        ) : (
                          <span>Loading...</span>
                        )}
                      </div>
                    )}
                  </div>

                  {submission.feedback && (
                    <div className="submission-feedback-clean">
                      <strong>Feedback:</strong> {submission.feedback}
                    </div>
                  )}
                </div>
              );

              const renderPendingReviewSection = (submission: TaskSubmission) => (
                <div key={submission.id} className="submission-card-clean">
                  <div className="submission-header-clean">
                    <span className="submission-user-clean">
                      Anonymous User #{submission.user_id.slice(-8)}
                    </span>
                    <span className="submission-date-clean">
                      {new Date(submission.submitted_at).toLocaleDateString()}
                    </span>
                  </div>

                  <div className="submission-proof-clean">
                    {submission.verification_type === 'email_username' && submission.proof?.email_username && (
                      <div className="proof-content">
                        <strong>Email/Username:</strong> {submission.proof.email_username}
                      </div>
                    )}
                    {submission.verification_type === 'text_response' && submission.proof?.text_response && (
                      <div className="proof-content">
                        <strong>Response:</strong> {submission.proof.text_response}
                      </div>
                    )}
                    {submission.verification_type === 'screenshot' && submission.proof?.screenshot_url && (
                      <div className="proof-content">
                        <strong>Screenshot:</strong>
                        {submissionScreenshotUrls[submission.id] ? (
                          <img
                            src={submissionScreenshotUrls[submission.id]!}
                            alt="Verification screenshot"
                            className="proof-screenshot-clean"
                          />
                        ) : (
                          <span>Loading...</span>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="review-section-clean">
                    <textarea
                      className="review-feedback-input"
                      value={reviewFeedback}
                      onChange={(e) => setReviewFeedback(e.target.value)}
                      placeholder="Optional feedback..."
                      rows={2}
                    />
                    <div className="review-actions-clean">
                      <button
                        className="btn-approve"
                        onClick={() => handleReviewSubmission(submission.user_id, true)}
                      >
                        Approve
                      </button>
                      <button
                        className="btn-reject"
                        onClick={() => handleReviewSubmission(submission.user_id, false)}
                      >
                        Reject
                      </button>
                    </div>
                  </div>
                </div>
              );

              return (
                <>
                  {/* Status Summary with Clickable Counts */}
                  <div className="submissions-summary-new">
                    <div
                      className={`status-count-item approved ${approvedSubmissions.length === 0 ? 'disabled' : ''}`}
                      onClick={approvedSubmissions.length > 0 ? () => toggleSection('approved') : undefined}
                    >
                      <span className="count">{approvedSubmissions.length}</span>
                      <span className="label">Approved</span>
                      {approvedSubmissions.length > 0 && (
                        <i className={`bi ${expandedSections.approved ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                      )}
                    </div>

                    <div
                      className={`status-count-item rejected ${rejectedSubmissions.length === 0 ? 'disabled' : ''}`}
                      onClick={rejectedSubmissions.length > 0 ? () => toggleSection('rejected') : undefined}
                    >
                      <span className="count">{rejectedSubmissions.length}</span>
                      <span className="label">Rejected</span>
                      {rejectedSubmissions.length > 0 && (
                        <i className={`bi ${expandedSections.rejected ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                      )}
                    </div>

                    <div
                      className={`status-count-item pending ${pendingSubmissions.length === 0 ? 'disabled' : ''}`}
                      onClick={pendingSubmissions.length > 0 ? () => toggleSection('pending') : undefined}
                    >
                      <span className="count">{pendingSubmissions.length}</span>
                      <span className="label">Pending Review</span>
                      {pendingSubmissions.length > 0 && (
                        <i className={`bi ${expandedSections.pending ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                      )}
                    </div>
                  </div>

                  {/* Approved Submissions */}
                  {expandedSections.approved && approvedSubmissions.length > 0 && (
                    <div className="submissions-list-clean">
                      {approvedSubmissions.map(renderSubmissionCard)}
                    </div>
                  )}

                  {/* Rejected Submissions */}
                  {expandedSections.rejected && rejectedSubmissions.length > 0 && (
                    <div className="submissions-list-clean">
                      {rejectedSubmissions.map(renderSubmissionCard)}
                    </div>
                  )}

                  {/* Pending Submissions */}
                  {expandedSections.pending && pendingSubmissions.length > 0 && (
                    <div className="submissions-list-clean">
                      {pendingSubmissions.map(renderPendingReviewSection)}
                    </div>
                  )}

                  {/* Show message when no submissions */}
                  {submissions.length === 0 && (
                    <div className="no-submissions">
                      <i className="bi bi-inbox"></i>
                      <p>No submissions received yet</p>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        )}
      </ResponsiveContainer>
    </div>
  );
};

export default TaskDetailPage;
